import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toPng } from "html-to-image";
import { toast } from "sonner";
import { Download, Loader2, Upload, Sparkles, IdCard, Link as LinkIcon } from "lucide-react";
import { CARD_TEMPLATES } from "@/components/homes/cards/AgentCards";
import { homesAgent, homesShorten } from "@/lib/api";

const EMPTY = {
  name: "", title: "Real Estate Agent", brokerage: "", phone: "", email: "",
  website: "", tagline: "", address: "", photo: "",
};

const stripProto = (u) => (u || "").replace(/^https?:\/\//, "").replace(/\/$/, "");

export default function AgentCardStudio() {
  const [params] = useSearchParams();
  const agentId = params.get("agent");
  const [form, setForm] = useState(EMPTY);
  const [template, setTemplate] = useState("classic");
  const [busy, setBusy] = useState(false);
  const [profileUrl, setProfileUrl] = useState("");
  const [shortLink, setShortLink] = useState(null); // { short_url, slug }
  const [shortening, setShortening] = useState(false);
  const cardRef = useRef(null);
  const fileRef = useRef(null);

  // Prefill from an agent profile when arriving via ?agent=<id>
  useEffect(() => {
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    if (!agentId) return;
    setProfileUrl(`${origin}/homes/agents/${agentId}`);
    homesAgent(agentId).then((d) => {
      const a = d.agent || {};
      setForm((f) => ({
        ...f,
        name: a.name || f.name,
        title: a.title || f.title,
        brokerage: a.brokerage || f.brokerage,
        phone: a.phone || f.phone,
        email: a.email || f.email,
        address: [a.city_name, a.state_name].filter(Boolean).join(", ") || f.address,
        photo: a.photo?.image_url || f.photo,
      }));
    }).catch(() => {});
  }, [agentId]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // A branded link is only valid for its exact target — drop it if the website changes.
  useEffect(() => { setShortLink(null); }, [form.website, profileUrl]);

  const onUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 6 * 1024 * 1024) { toast.error("Image too large (max 6MB)"); return; }
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, photo: reader.result }));
    reader.readAsDataURL(file);
  };

  // QR target: agent's website if provided, else their card/profile on our site.
  const rawTarget = form.website?.trim() || profileUrl || "https://homespulse.pro";
  const qrValue = shortLink?.short_url || rawTarget;
  const cardData = {
    ...form,
    website: stripProto(form.website),
    qrValue,
    splinkUrl: stripProto(qrValue),
  };

  const brand = async () => {
    let target = rawTarget;
    if (!/^https?:\/\//i.test(target)) target = `https://${target}`;
    setShortening(true);
    try {
      const res = await homesShorten(target);
      if (res.ok) {
        setShortLink({ short_url: res.short_url, slug: res.slug });
        toast.success(res.existing ? "Branded link reused — scans are tracked" : "Branded SpLink created — QR now tracks scans");
      } else {
        toast.message(res.message || "Couldn't brand this link right now.");
      }
    } catch {
      toast.error("Couldn't reach the link service. Please try again.");
    } finally { setShortening(false); }
  };

  const Active = CARD_TEMPLATES.find((t) => t.id === template) || CARD_TEMPLATES[0];

  const download = async () => {
    if (!cardRef.current) return;
    setBusy(true);
    try {
      const dataUrl = await toPng(cardRef.current, { pixelRatio: 2, backgroundColor: "#ffffff" });
      const link = document.createElement("a");
      link.download = `homespulse-card-${template}.png`;
      link.href = dataUrl;
      link.click();
      toast.success("Card downloaded");
    } catch (err) {
      toast.error("Couldn't export — try uploading the photo instead of a remote link.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="agent-card-studio">
      {/* Hero */}
      <section className="bg-[#0F172A] text-white">
        <div className="mx-auto max-w-[1400px] px-4 py-12">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 text-xs font-bold uppercase tracking-widest text-[#93B4FF]">
            <IdCard className="h-3.5 w-3.5" /> Agent Card Studio
          </span>
          <h1 className="mt-3 text-4xl font-black md:text-5xl">Design your digital business card</h1>
          <p className="mt-2 max-w-2xl text-white/70">
            Pick a template, add your details, and download a share-ready card with a scannable QR code — free to try.
          </p>
        </div>
      </section>

      <div className="mx-auto grid max-w-[1400px] gap-8 px-4 py-10 lg:grid-cols-[380px_1fr]">
        {/* Form */}
        <div className="space-y-4" data-testid="studio-form">
          <Field label="Full name" v={form.name} onChange={set("name")} placeholder="Jessica Miller" testid="studio-name" />
          <Field label="Title" v={form.title} onChange={set("title")} placeholder="Real Estate Agent" testid="studio-title" />
          <Field label="Brokerage" v={form.brokerage} onChange={set("brokerage")} placeholder="Homes & Co" testid="studio-brokerage" />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Phone" v={form.phone} onChange={set("phone")} placeholder="(555) 123-4567" testid="studio-phone" />
            <Field label="Email" v={form.email} onChange={set("email")} placeholder="you@brokerage.com" testid="studio-email" />
          </div>
          <Field label="Website (QR target)" v={form.website} onChange={set("website")} placeholder={profileUrl ? "Leave blank → your HomesPulse profile" : "yourwebsite.com"} testid="studio-website" />
          <p className="-mt-2 text-xs text-slate-500">
            {form.website?.trim()
              ? "QR code points to your website."
              : profileUrl
                ? "No website — QR points to your HomesPulse profile."
                : "QR points to HomesPulse.pro until you add a website."}
          </p>

          {shortLink ? (
            <div className="-mt-1 flex items-center justify-between gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2" data-testid="studio-splink-badge">
              <span className="flex items-center gap-1.5 text-xs font-semibold text-emerald-800"><LinkIcon className="h-3.5 w-3.5" /> Branded & tracked · {stripProto(shortLink.short_url)}</span>
            </div>
          ) : (
            <button onClick={brand} disabled={shortening}
              className="-mt-1 flex w-full items-center justify-center gap-2 rounded-lg border border-[#2563EB] bg-white px-3 py-2 text-sm font-bold text-[#2563EB] transition-colors hover:bg-[#EEF2FF] disabled:opacity-60"
              data-testid="studio-brand-btn">
              {shortening ? <Loader2 className="h-4 w-4 animate-spin" /> : <LinkIcon className="h-4 w-4" />}
              Brand & track this QR with SpLink
            </button>
          )}
          <Field label="Address / area" v={form.address} onChange={set("address")} placeholder="Austin, TX" testid="studio-address" />
          <Field label="Tagline (optional)" v={form.tagline} onChange={set("tagline")} placeholder="Helping you find home." testid="studio-tagline" />

          <div>
            <label className="mb-1.5 block text-xs font-bold uppercase tracking-widest text-slate-500">Photo</label>
            <input ref={fileRef} type="file" accept="image/*" onChange={onUpload} className="hidden" data-testid="studio-photo-input" />
            <button onClick={() => fileRef.current?.click()} className="flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-700 transition-colors hover:border-[#2563EB] hover:text-[#2563EB]" data-testid="studio-photo-btn">
              <Upload className="h-4 w-4" /> {form.photo ? "Change photo" : "Upload photo"}
            </button>
          </div>
        </div>

        {/* Preview + templates */}
        <div>
          {/* Template picker */}
          <div className="mb-4 flex flex-wrap gap-2" data-testid="studio-templates">
            {CARD_TEMPLATES.map((t) => (
              <button key={t.id} onClick={() => setTemplate(t.id)}
                className={`rounded-full px-4 py-2 text-sm font-bold transition-colors ${template === t.id ? "bg-[#2563EB] text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}
                data-testid={`studio-template-${t.id}`}>
                {t.name}
              </button>
            ))}
          </div>

          {/* Live preview (scaled to fit) */}
          <div className="flex justify-center rounded-2xl border border-slate-200 bg-slate-100 p-4">
            <div className="hp-frame">
              <div className="hp-scale">
                <div ref={cardRef} data-testid="studio-preview">
                  <Active.Component data={cardData} />
                </div>
              </div>
            </div>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <button onClick={download} disabled={busy}
              className="inline-flex items-center gap-2 rounded-full bg-[#2563EB] px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-[#1D4ED8] disabled:opacity-60"
              data-testid="studio-download-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              Download PNG
            </button>
            <span className="inline-flex items-center gap-1.5 text-xs text-slate-500">
              <Sparkles className="h-3.5 w-3.5 text-[#2563EB]" /> 640×384 · exported at 2× for print & social
            </span>
          </div>
        </div>
      </div>

      {/* Responsive scale: shrink the 640px card on narrow screens without leaving empty space */}
      <style>{`
        .hp-frame { --hp-scale: 1; width: calc(640px * var(--hp-scale)); height: calc(384px * var(--hp-scale)); overflow: hidden; }
        .hp-scale { width: 640px; transform: scale(var(--hp-scale)); transform-origin: top left; }
        @media (max-width: 720px) { .hp-frame { --hp-scale: 0.5; } }
        @media (min-width: 721px) and (max-width: 1120px) { .hp-frame { --hp-scale: 0.72; } }
      `}</style>
    </div>
  );
}

function Field({ label, v, onChange, placeholder, testid }) {
  return (
    <div>
      <label className="mb-1.5 block text-xs font-bold uppercase tracking-widest text-slate-500">{label}</label>
      <input value={v} onChange={onChange} placeholder={placeholder}
        className="w-full rounded-xl border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-[#0F172A] outline-none transition-colors focus:border-[#2563EB]"
        data-testid={testid} />
    </div>
  );
}
